// Плашка «идёт тренировка» на всех экранах, кроме самой тренировки: название, сколько идёт,
// упражнения «5/12», таймер отдыха; у неначатой — «не начата». На телефоне висит над нижним
// меню, на десктопе — справа внизу. Заодно, пока есть связь, подтягивает в кэш всё, что
// понадобится в зале без сети, и забывает неначатую тренировку, подготовленную не сегодня.

import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { Icon } from '@/components/ui/Icon'
import { cn } from '@/lib/cn'
import { formatClock } from '@/lib/format'
import { useRestTimer } from '@/lib/restTimer'
import {
  isStaleDraft,
  setActiveWorkout,
  useActiveWorkout,
  workoutTitle,
  type ActiveWorkout,
} from '@/offline/activeSession'

import { useNow, useWorkoutPrefetch } from './data'
import { exerciseProgress, workoutElapsed } from './logic'

export function Banner() {
  useWorkoutPrefetch()
  const { workout } = useActiveWorkout()
  const { pathname } = useLocation()
  const minute = useNow(60_000)
  const stale = workout !== null && isStaleDraft(workout, new Date(minute))
  useEffect(() => {
    if (stale) void setActiveWorkout(null)
  }, [stale])
  if (!workout || stale || pathname === '/workout') return null
  return (
    // data-workout-dock: по нему AppShell добавляет снизу место под плашку.
    <div
      data-workout-dock
      className="fixed inset-x-0 bottom-[calc(3.5rem+1px+env(safe-area-inset-bottom))] z-30 px-2 pb-2 lg:bottom-4 lg:left-auto lg:right-6 lg:w-96 lg:p-0"
    >
      {workout.draft ? (
        <DraftDock title={workoutTitle(workout)} total={workout.exercises.length} />
      ) : (
        <WorkoutDock workout={workout} title={workoutTitle(workout)} {...exerciseProgress(workout)} />
      )}
    </div>
  )
}

const DOCK_CLASS =
  'mx-auto flex max-w-xl items-center gap-3 rounded-2xl border border-accent/40 bg-surface bg-linear-to-r from-accent-soft to-accent-soft py-2.5 pl-3 pr-2 shadow-lg shadow-black/30 active:bg-surface-2'

/** Неначатая тренировка: напоминание, что она ждёт «Старт». */
function DraftDock({ title, total }: { title: string; total: number }) {
  return (
    <Link to="/workout" className={DOCK_CLASS}>
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
        <Icon name="workout" size={18} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">
            <span className="sr-only">Тренировка не начата: </span>
            {title}
          </span>
          <span className="shrink-0 text-xs font-medium text-muted">не начата</span>
        </span>
        <span className="text-xs text-muted">
          Упражнения <span className="font-medium tabular-nums text-fg">{total}</span> · начни кнопкой «Старт»
        </span>
      </span>
      <Icon name="chevron-right" size={20} className="shrink-0 text-accent" />
    </Link>
  )
}

function WorkoutDock({ workout, title, done, total }: { workout: ActiveWorkout; title: string; done: number; total: number }) {
  const now = useNow(1000)
  const timer = useRestTimer()
  const elapsed = workoutElapsed(workout, now)
  const paused = Boolean(workout.paused_at)
  return (
    <Link to="/workout" className={DOCK_CLASS}>
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-accent-fg">
        <Icon name="workout" size={18} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">
            <span className="sr-only">Идёт тренировка: </span>
            {title}
          </span>
          <span className={cn('shrink-0 text-sm font-semibold tabular-nums', paused ? 'text-muted' : 'text-accent')}>
            {paused ? <Icon name="pause" size={12} className="mr-1 inline align-[-1px]" title="На паузе" /> : null}
            {formatClock(elapsed)}
          </span>
        </span>
        <span className="flex items-baseline gap-2 text-xs text-muted">
          <span className="min-w-0 flex-1 truncate">
            Упражнения <span className="font-medium tabular-nums text-fg">{done}/{total}</span>
          </span>
          {timer.running ? (
            timer.remaining > 0 ? (
              <span className="shrink-0 font-medium tabular-nums text-accent">
                отдых {formatClock(timer.remaining)}
              </span>
            ) : (
              <span className="shrink-0 rounded bg-danger px-1 font-medium tabular-nums text-danger-fg">
                отдых −{formatClock(timer.overtime)}
              </span>
            )
          ) : null}
        </span>
      </span>
      <Icon name="chevron-right" size={20} className="shrink-0 text-accent" />
      <span className="sr-only">Вернуться к тренировке</span>
    </Link>
  )
}
